import React, { Fragment, useEffect, useState } from 'react';
import { Box, Button, Dialog, Grid, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { withStyles } from '@mui/styles';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import { getAllocationVariances } from 'src/services/ResourcePoolService';
import moment from 'moment';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import Indicator from './Indicator';
import CloseIcon from '@mui/icons-material/Close';

const ResourcesModal = ({ open, toggle, startDate, endDate, type, utilized, allocationType }) => {
  const dispatch = useDispatch();
  const [resources, setResources] = useState<any>();
  const [expand, setExpand] = useState<any>();
  const [expandUtilized, setExpandUtilized] = useState<any>();

  useEffect(() => {
    if(open){
      dispatch(AppActions.showLoader());
      const fetchAllResources = () => {
        const isShared = type.includes('SHARED_SERVICE');
        const isDelivery = type.includes('DELIVERY');

        getAllocationVariances(moment(startDate).format('MM/DD/YYYY'), moment(endDate).format('MM/DD/YYYY'), isShared, isDelivery, allocationType).then((res) => {
          const data = res.data
          setResources(data);
          dispatch(AppActions.hideLoader());
        });
      };
      fetchAllResources();
    }
  }, [open]);


  const handleExpand = (id) => {
    setExpand(
      {
        id: id,
        expand: expand?.id === id ? !expand.expand : true
      }
    );
  }

  const handleExpandUtilized = (id) => {
    setExpandUtilized(
      {
        id: id,
        expandUtilized: expandUtilized?.id === id ? !expandUtilized.expandUtilized : true
      }
    );
  }


  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);


  const CustomTableColumnCell = ({content}) => {
    return(
      <TableColumnCell>
        <Typography
          paddingBottom="0.25rem"
          paddingTop="0.5rem"
          variant="body2"
          style={{ fontSize: '11px' }}
          gutterBottom
          noWrap
        >
          {content}
        </Typography>
      </TableColumnCell>
    );
  }

  const calculatePercentage = (itemA: any, type: string) => {
    const allocations = itemA?.allocations?.filter(
      all => all?.allocatedProject?.toLowerCase() !== 'bench'
    ) || [];

    const total = allocations.reduce((sum, obj) => {
      return sum + (
        (obj?.billablePercentage || 0) +
        (obj?.nonBillablePercentage || 0) +
        (obj?.shadowPercentage || 0) +
        (obj?.supportPercentage || 0) +
        (obj?.timeAndManagementPercentage || 0)
      );
    }, 0);

    const roundedTotal = Number(total.toFixed(2));

    const value = type === 'underutlized'
      ? (100 - roundedTotal)
      : (roundedTotal - 100);

    return formatPercentage(value);
  };

  const formatPercentage = (value) => {
    const num = Number(value.toFixed(2));
    return `${num % 1 === 0 ? num.toFixed(0) : num}%`;
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth scroll="paper" 
    onClose={(event, reason) => {
      if (reason !== "backdropClick") {
        toggle();
      }
    }}>
      <Grid item xs={12} sx={{ p: 3, pb: 2 }} display='flex' justifyContent='space-between' alignItems='center'>
        <Typography style={{ fontSize: '15px' }} variant="h3">
          Allocation Variance Report
        </Typography>
        <CloseIcon onClick={toggle} style={{cursor: 'pointer'}}/>
      </Grid>
      <Box style={{display: 'flex', flexDirection: 'row'}} px={3}>
        <Typography ml={1} style={{ fontWeight: '500', fontSize: '12px' }}>
          <span style={{fontWeight: 'bold'}}>Note:</span> Bench allocation percentages were not factored into the aggregation
        </Typography>
      </Box>
      <Box style={{display: 'flex', flexDirection: 'row'}} px={3}>
        <Indicator color='red' description='Inactive Resource'/>
        <Indicator color='orange' description='Inactive Project'/>
      </Box>
      <Grid item xs={12} sx={{ p: 3, pt: 0 }}>
      <TableContainer className="tableContainer underutilized-table">
        <Table style={{ borderCollapse: 'separate' }} size="small">
          <TableHead>
            <CustomTableRow>
              <TableHeaderCell content="" smallFont/>
              <TableHeaderCell content="Resource Name" smallFont />
              <TableHeaderCell content="Project" smallFont />
              <TableHeaderCell content="Client" smallFont />
              <TableHeaderCell content="Project Role" smallFont />
              <TableHeaderCell content="Billable" smallFont />
              <TableHeaderCell content="Non-Billable" smallFont />
              <TableHeaderCell content="Shadow" smallFont />
              <TableHeaderCell content="Support" smallFont />
              <TableHeaderCell content="T&M" smallFont />
            </CustomTableRow>
          </TableHead>
          <TableBody>
            { resources && Object.entries(resources).map((item: any, index) => {
              return(
                <Fragment>
                  <TableRow 
                    key={index} 
                    hover
                    className='utilized-row'>
                    <TableColumnCell>
                      {item[1]?.length > 0 && 
                        <Button
                          sx={{
                            mt: { xs: 2, md: 0 },
                            fontSize: '11px',
                            fontWeight: 'normal',
                            width: '14px',
                            height: '14px',
                            padding: 0,
                            minWidth: 'unset',
                            borderRadius: '2px',
                            cursor: 'pointer'
                          }}
                          onClick={e => handleExpandUtilized(index)}
                          variant="contained"
                          startIcon={expandUtilized?.id === index && expandUtilized?.expandUtilized ? <HorizontalRuleIcon fontSize="small" style={{fontSize: '14px'}}/> : 
                          <AddTwoToneIcon fontSize="small" style={{fontSize: '14px'}}/>}
                        />
                      }
                    </TableColumnCell>
                    <TableColumnCell>
                      <Grid>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {item[0] === 'overutlized' ? 'Overutilized Resources' : 'Underutilized Resources'}
                        </Typography>
                        {item[1]?.length === 0 && 
                        <Typography noWrap style={{fontSize: '9px', color: 'blue'}}>
                          {`(There is No ${item[0] === 'overutlized' ? 'Overutilized Resource' : 'Underutilized Resource'} found for the given date range)`}
                        </Typography>}
                      </Grid>                      
                    </TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                    <TableColumnCell></TableColumnCell>
                  </TableRow>
                  {expandUtilized?.id === index && expandUtilized?.expandUtilized && item[1]?.length > 0 && 
                    item[1]?.map((itemA, i) => {
                    return(
                      <Fragment>
                        <TableRow 
                          key={i} 
                          hover
                          className={(expand?.id === i && expand?.expand) ? 'selected-row' : ''}>
                          <TableColumnCell>
                            {itemA?.allocations.length > 0 && 
                              <Button
                                sx={{
                                  mt: { xs: 2, md: 0 },
                                  fontSize: '11px',
                                  fontWeight: 'normal',
                                  width: '14px',
                                  height: '14px',
                                  padding: 0,
                                  minWidth: 'unset',
                                  borderRadius: '2px',
                                  cursor: 'pointer'
                                }}
                                onClick={e => handleExpand(i)}
                                variant="contained"
                                startIcon={expand?.id === i && expand?.expand ? <HorizontalRuleIcon fontSize="small" style={{fontSize: '14px'}}/> : 
                                <AddTwoToneIcon fontSize="small" style={{fontSize: '14px'}}/>}
                              />
                            }
                          </TableColumnCell>
                          <TableColumnCell>
                            <div style={{display: 'flex', alignItems: 'center'}}>
                              {!itemA?.isActiveEmployee && (
                                <div style={{width: '25px'}} className='border-0'>
                                  <Box style={{display: 'flex', flexDirection: 'row', alignItems: 'left'}}>
                                    <Indicator size={5} color='red'/>
                                  </Box>
                                </div>
                              )}
                              <div className='border-0'>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body2"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {itemA?.name}
                                </Typography>
                              </div>
                            </div>
                          </TableColumnCell>
                          <TableColumnCell>
                            <Typography
                              paddingBottom="0.25rem"
                              paddingTop="0.5rem"
                              variant="body2"
                              style={{ fontSize: '11px', fontWeight: 'bold', color: 'blue' }}
                              color="text.primary"
                              gutterBottom
                              noWrap
                            >
                              {item[0] === 'overutlized' ? 'Overutilized by' : 'Underutilized by'}
                            </Typography>
                          </TableColumnCell>
                          <TableColumnCell colSpan={2}></TableColumnCell>
                          <TableColumnCell colSpan={5}>
                            <Typography
                              paddingBottom="0.25rem"
                              paddingTop="0.5rem"
                              variant="body2"
                              style={{ fontSize: '11px', fontWeight: 'bold', color: 'blue' }}
                              color="text.primary"
                              gutterBottom
                              noWrap
                            >
                              {calculatePercentage(itemA, item[0])}
                            </Typography>
                          </TableColumnCell>
                        </TableRow>
                        {
                          expand?.id === i && expand?.expand && itemA?.allocations.length > 0 && itemA?.allocations.map((res, i) => {
                            return (
                              <TableRow
                                hover
                                key={i}
                                className={itemA?.allocations.length - 1 === i ? `project-row project-final-row ${res?.allocatedProject.toLowerCase() === 'bench' && 'bench-row'}` : 
                                i === 0 ? `project-row project-first-row ${res?.allocatedProject.toLowerCase() === 'bench' && 'bench-row'}` : 
                                `project-row ${res?.allocatedProject.toLowerCase() === 'bench' && 'bench-row'}`}
                              >
                                <TableColumnCell></TableColumnCell>
                                <TableColumnCell></TableColumnCell>
                                <TableColumnCell>
                                  <div style={{display: 'flex', alignItems: 'center'}}>
                                    {!res?.isActiveProject && (
                                      <div style={{width: '25px'}} className='border-0'>
                                        <Box style={{display: 'flex', flexDirection: 'row', alignItems: 'left'}}>
                                          {!res?.isActiveProject && <Indicator size={5} color='orange'/>}
                                        </Box>
                                      </div>)
                                    }
                                    <div className='border-0'>
                                      <Typography
                                        paddingBottom="0.25rem"
                                        paddingTop="0.5rem"
                                        variant="body2"
                                        style={{ fontSize: '11px' }}
                                        color="text.primary"
                                        gutterBottom
                                        noWrap
                                      >
                                        {res?.allocatedProject}
                                      </Typography>
                                    </div>
                                  </div>
                                </TableColumnCell>
                                <CustomTableColumnCell content={res?.client}/>
                                <CustomTableColumnCell content={res?.projectRole}/>
                                <CustomTableColumnCell content={res?.billablePercentage + '%'}/>
                                <CustomTableColumnCell content={res?.nonBillablePercentage + '%'}/>
                                <CustomTableColumnCell content={res?.shadowPercentage + '%'}/>
                                <CustomTableColumnCell content={res?.supportPercentage + '%'}/>
                                <CustomTableColumnCell content={res?.timeAndManagementPercentage + '%'}/>
                              </TableRow>
                            );
                          })
                        }
                      </Fragment>);
                    })
                  }
                </Fragment>);
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
      </Grid>
    </Dialog>
  );
};

export default ResourcesModal;
